import { Box, Button, Skeleton, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import XMLFileUpload from './XMLFileUpload'
import Export from './Export'
import { useTranslation } from 'react-i18next'
import { downloadConfiguration, getCustomerExtractStatus, uploadConfiguration } from './state/actions'
import { useDispatch, useSelector } from 'react-redux'
import { useNotifier } from '@cbgms/base/notifier'
import { IRootReducerState } from 'state/reducers'
import moment from 'moment'

interface ICustomerExtractStatus {
  OuName: string
  Status: string
  RecordsProcessed: number
  ProcessingDate: string
}

const XMLUpload = () => {
  const { t } = useTranslation()
  const [openUploadXML, setOpenUploadXML] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [extractStatus, setExtractStatus] = useState<ICustomerExtractStatus | null>(null)
  const [isStatusLoading, setIsStatusLoading] = useState(false)
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const ouName = useSelector((state: IRootReducerState) => state.auth.user.ou_name)

  useEffect(() => {
    if (!ouName) return
    setIsStatusLoading(true)
    ;(dispatch(getCustomerExtractStatus(ouName)) as unknown as Promise<{ Data: ICustomerExtractStatus }>)
      .then(data => setExtractStatus(data.Data))
      .catch(() => {
        // silently fail — status is informational; page remains functional
      })
      .finally(() => setIsStatusLoading(false))
  }, [dispatch, ouName])

  const handleDownload = async () => {
    setIsLoading(true)
    try {
      const response: unknown = await dispatch(downloadConfiguration())

      const url = window.URL.createObjectURL(new Blob([response as Blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'configuration.xml')
      document.body.appendChild(link)
      link.click()
      link.remove()

      notifier.success(t('LKQConfig.XMLDownloadSuccess'))
    } catch (err) {
      notifier.error(t('LKQConfig.XMLDownloadFailed'))
    } finally {
      setIsLoading(false)
    }
  }
  const confirmUploadXML = async (xmlFile: File) => {
    setIsLoading(true)
    try {
      await dispatch(uploadConfiguration(xmlFile))
      notifier.success(t('LKQConfig.XMLUploadSuccess'))
      setOpenUploadXML(false)
    } catch (error) {
      notifier.error(t('LKQConfig.XMLUploadFailed'))
    } finally {
      setIsLoading(false)
    }
  }
  return (
    <div>
      <Typography variant='h6' sx={{ marginBottom: 2, fontSize: 20, fontWeight: 700 }}>
        {t('LKQConfig.XMLUpload')}
      </Typography>
      <p>{t('LKQConfig.XMLUploadDescription')}</p>
      <Box marginRight={2}>
        <Button variant='contained' color='primary' onClick={() => setOpenUploadXML(true)}>
          {t('LKQConfig.UploadXML')}
        </Button>
        <Button sx={{ marginLeft: '10px' }} variant='contained' color='primary' onClick={() => handleDownload()}>
          {t('LKQConfig.DownloadXML')}
        </Button>
        <Export />
      </Box>

      <Box sx={{ marginTop: 3 }}>
        {isStatusLoading ? (
          <Skeleton variant='text' width={500} />
        ) : extractStatus ? (
          <Typography variant='body2'>
            {t('LKQConfig.CustomerExtractStatus', {
              status: extractStatus.Status,
              date:
                extractStatus.ProcessingDate && !extractStatus.ProcessingDate.startsWith('0001')
                  ? moment(extractStatus.ProcessingDate).format('DD.MM.YYYY – hh:mm a')
                  : moment().subtract(1, 'day').format('DD.MM.YYYY'),
              records: extractStatus.RecordsProcessed.toLocaleString(),
              ouName: extractStatus.OuName
            })}
          </Typography>
        ) : null}
      </Box>

      <XMLFileUpload open={openUploadXML} onCancel={() => setOpenUploadXML(false)} onConfirm={confirmUploadXML} isLoading={isLoading} />
    </div>
  )
}
export default XMLUpload
